import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { BUSINESS } from "@/lib/site-data";
import {
  findMatrixRow,
  relatedProblems,
  type SeoMatrixRow,
} from "@/lib/seo-matrix";
import { CallNowButton } from "@/components/CallNowButton";
import { FaqAccordion } from "@/components/FaqAccordion";
import { InternalLinkNetwork } from "@/components/InternalLinkNetwork";

function canonicalFor(row: Pick<SeoMatrixRow, "trade" | "city" | "problem">) {
  return `https://lehitradessaeo.com/seo/${row.trade}/${row.city}/${row.problem}`;
}

export const Route = createFileRoute("/seo/$trade/$city/$problem")({
  loader: ({ params }) => {
    const row = findMatrixRow(params.trade, params.city, params.problem);
    if (!row) throw notFound();
    return { row };
  },
  head: ({ loaderData }) => {
    if (!loaderData)
      return {
        meta: [
          { title: "Not found" },
          { name: "robots", content: "noindex" },
        ],
      };
    const { row } = loaderData;
    const title = `${row.problemLabel} in ${row.cityLabel}, UT | ${row.tradeLabel} Marketing | ${BUSINESS.shortName}`;
    const description = `Rank ${row.cityLabel} homeowners searching for ${row.problemLabel.toLowerCase()}. SEO, Google Business Profile, and Local Service Ads built for ${row.cityLabel} ${row.tradeLabel.toLowerCase()} companies.`;
    const canonical = canonicalFor(row);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: canonical },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            serviceType: `${row.problemLabel} — ${row.tradeLabel}`,
            areaServed: { "@type": "City", name: `${row.cityLabel}, UT` },
            provider: { "@id": "https://lehitradessaeo.com/#organization" },
            url: canonical,
            description,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://lehitradessaeo.com/" },
              { "@type": "ListItem", position: 2, name: "Local trade pages", item: "https://lehitradessaeo.com/seo" },
              {
                "@type": "ListItem",
                position: 3,
                name: `${row.tradeLabel} SEO in ${row.cityLabel}`,
                item: `https://lehitradessaeo.com/seo/${row.trade}/${row.city}`,
              },
              { "@type": "ListItem", position: 4, name: row.problemLabel, item: canonical },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-6 py-20 text-center">
      <h1 className="font-display text-3xl">Page not found</h1>
      <p className="mt-2 text-muted-foreground">
        <Link to="/seo" className="underline">
          Browse all local trade pages
        </Link>
      </p>
    </main>
  ),
  component: SeoProblemPage,
});

function SeoProblemPage() {
  const { row } = Route.useLoaderData();
  const canonical = canonicalFor(row);
  const related = relatedProblems(row, 10);
  const searchTerm = `${row.problemLabel.toLowerCase()} ${row.cityLabel}`;

  const faqs = [
    {
      q: `How fast can a ${row.cityLabel} ${row.tradeLabel.toLowerCase()} rank for "${row.problemLabel.toLowerCase()}"?`,
      a: `Most ${row.cityLabel} ${row.tradeLabel.toLowerCase()} companies see Map Pack movement on "${searchTerm}" in 60–90 days once we ship a dedicated landing page, tune the Google Business Profile, and layer Local Service Ads over the top.`,
    },
    {
      q: `Do ${row.cityLabel} homeowners actually search "${row.problemLabel.toLowerCase()}"?`,
      a: `Yes — ${row.climate} That mix of ${row.homeAge.toLowerCase()} drives steady, seasonal demand for ${row.problemLabel.toLowerCase()} across ${row.neighborhoods.slice(0, 2).join(" and ")}.`,
    },
    {
      q: `What does a ${row.problemLabel} marketing program cost in ${row.cityLabel}?`,
      a: `Programs run $1,500–$4,500/mo depending on how aggressively you want to own ${row.problemLabel.toLowerCase()} across ${row.cityLabel} and whether Local Service Ads are included. Full benchmarks are on our ROI calculator.`,
    },
    {
      q: `Will you write ${row.cityLabel}-specific content, or reuse templates?`,
      a: `Every ${row.cityLabel} page ships with real local proof — ${row.neighborhoods.join(", ")}, ${row.homeAge.toLowerCase()}, and job photos from the neighborhoods you actually serve.`,
    },
  ];

  return (
    <main className="mx-auto max-w-3xl px-6 py-16">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
        {row.cityLabel}, UT · {row.tradeLabel}
      </p>
      <h1 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">
        {row.problemLabel} Marketing for {row.cityLabel} {row.tradeLabel} Companies
      </h1>
      <p className="mt-5 text-xl text-muted-foreground">
        We help {row.cityLabel} {row.tradeLabel.toLowerCase()} businesses rank
        for "{searchTerm}" — SEO, Google Business Profile, and Local Service Ads
        tuned to the way {row.cityLabel} homeowners actually search.
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        <Link
          to="/contact"
          className="inline-flex items-center rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Get a free {row.cityLabel} audit
        </Link>
        <CallNowButton variant="primary" label={`Call ${BUSINESS.phone}`} />
      </div>

      <section className="mt-12">
        <h2 className="font-display text-2xl tracking-tight">
          Why {row.cityLabel} homeowners search "{row.problemLabel.toLowerCase()}"
        </h2>
        <ul className="mt-4 space-y-3 text-base text-foreground/90">
          <li>
            <strong>Market:</strong> {row.climate}
          </li>
          <li>
            <strong>Housing stock:</strong> {row.homeAge}
          </li>
          <li>
            <strong>Key neighborhoods:</strong> {row.neighborhoods.join(", ")}.
          </li>
        </ul>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-2xl tracking-tight">
          What we build for {row.cityLabel} {row.tradeLabel.toLowerCase()}s
        </h2>
        <ul className="mt-4 space-y-3 text-base text-foreground/90">
          <li>
            A dedicated <strong>{row.problemLabel} in {row.cityLabel}</strong>{" "}
            landing page written for local intent — response time, pricing
            transparency, and neighborhood proof.
          </li>
          <li>
            Google Business Profile services + weekly posts targeting{" "}
            "{searchTerm}".
          </li>
          <li>
            Local Service Ads with the Google Guaranteed badge, filtered to
            {" "}{row.cityLabel} and its surrounding zip codes.
          </li>
          <li>
            Review-velocity workflow that asks {row.cityLabel} customers for a
            review with the "{row.problemLabel.toLowerCase()}" keyword baked in.
          </li>
          <li>
            LocalBusiness + Service + FAQPage schema so ChatGPT, Perplexity, and
            Google's AI Overviews cite you as the {row.cityLabel} answer.
          </li>
        </ul>
      </section>

      <FaqAccordion
        title={`${row.cityLabel} ${row.problemLabel} FAQ`}
        items={faqs}
        pageUrl={canonical}
      />

      <InternalLinkNetwork
        seoTrade={row.trade}
        cityLabel={row.cityLabel}
        focusText={`${row.problemLabel} ${row.tradeLabel}`}
      />

      <section className="mt-12 rounded-2xl border border-border bg-card p-8">
        <h3 className="font-display text-xl tracking-tight">
          Free {row.cityLabel} {row.problemLabel.toLowerCase()} audit
        </h3>
        <p className="mt-2 text-muted-foreground">
          We'll show you exactly where you rank for "{searchTerm}" today, who's
          outranking you, and the fastest path to the Map Pack.
        </p>
        <Link
          to="/contact"
          className="mt-5 inline-flex rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Request my audit
        </Link>
      </section>

      {related.length > 0 ? (
        <section className="mt-12">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            More {row.tradeLabel.toLowerCase()} services in {row.cityLabel}
          </p>
          <ul className="mt-3 grid gap-1 text-sm sm:grid-cols-2">
            {related.map((r) => (
              <li key={r.problem}>
                <Link
                  to="/seo/$trade/$city/$problem"
                  params={{ trade: r.trade, city: r.city, problem: r.problem }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  {r.problemLabel} in {r.cityLabel}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="mt-8">
        <Link
          to="/seo/$trade/$city"
          params={{ trade: row.trade, city: row.city }}
          className="text-sm text-muted-foreground underline"
        >
          ← All {row.tradeLabel} SEO services in {row.cityLabel}
        </Link>
      </section>
    </main>
  );
}